import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import adminService from '../services/adminService';
import { useAuth } from './AuthContext';

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const { user, refreshUser } = useAuth();
  
  // State
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [orgStats, setOrgStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.isSuperAdmin;

  // Fetch admin context on mount
  useEffect(() => {
    const fetchContext = async () => {
      if (!isSuperAdmin) {
        setLoading(false);
        return;
      }

      try {
        // adminService returns { success, data: { organizations, locations, ... } }
        const response = await adminService.getAdminContext();
        const data = response.data || {};

        setOrganizations(data.organizations || []);
        setAvailableLocations(data.locations || []);

        // Set selected org if any
        if (data.selectedOrganizationId) {
          const org = data.organizations?.find(o => o._id === data.selectedOrganizationId._id || o._id === data.selectedOrganizationId);
          setSelectedOrganization(org || data.selectedOrganizationId);
          setSelectedOrganizationId(data.selectedOrganizationId._id || data.selectedOrganizationId);
        }

        // Set selected location if any
        if (data.selectedLocationId) {
          const loc = data.locations?.find(l => l._id === data.selectedLocationId._id || l._id === data.selectedLocationId);
          setSelectedLocation(loc || data.selectedLocationId);
          setSelectedLocationId(data.selectedLocationId._id || data.selectedLocationId);
        }
      } catch (error) {
        console.error('Failed to fetch admin context:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContext();
  }, [isSuperAdmin, user?._id]);

  // Fetch platform stats
  const fetchPlatformStats = useCallback(async (params = {}) => {
    if (!isSuperAdmin) return;

    setStatsLoading(true);
    try {
      // adminService returns { success, data: { organizations, locations, users, ..., recentActivityPagination } }
      const response = await adminService.getPlatformStats(params);
      setPlatformStats(response.data);
    } catch (error) {
      console.error('Failed to fetch platform stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [isSuperAdmin]);

  // Fetch org stats
  const fetchOrgStats = useCallback(async (orgId) => {
    if (!isSuperAdmin || !orgId) {
      setOrgStats(null);
      return;
    }

    setStatsLoading(true);
    try {
      // adminService returns { success, data: {...} }
      const response = await adminService.getOrganizationStats(orgId);
      setOrgStats(response.data);
    } catch (error) {
      console.error('Failed to fetch org stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [isSuperAdmin]);

  // Select organization
  const selectOrganization = useCallback(async (orgId) => {
    if (!isSuperAdmin) return;

    try {
      // Call API to set context
      await adminService.setAdminContext({ organizationId: orgId || null });

      if (orgId) {
        // Find org and set it
        const org = organizations.find(o => o._id === orgId);
        setSelectedOrganization(org);
        setSelectedOrganizationId(orgId);

        // Fetch updated context to get locations
        // adminService returns { success, data: { organizations, locations, ... } }
        const response = await adminService.getAdminContext();
        const contextData = response.data || {};
        setAvailableLocations(contextData.locations || []);

        // Auto-select location if only one
        if (contextData.locations?.length === 1) {
          const loc = contextData.locations[0];
          setSelectedLocation(loc);
          setSelectedLocationId(loc._id);
        } else if (contextData.selectedLocationId) {
          const loc = contextData.locations?.find(l => l._id === contextData.selectedLocationId._id || l._id === contextData.selectedLocationId);
          setSelectedLocation(loc);
          setSelectedLocationId(contextData.selectedLocationId._id || contextData.selectedLocationId);
        } else {
          setSelectedLocation(null);
          setSelectedLocationId(null);
        }

        // Fetch org stats
        await fetchOrgStats(orgId);
      } else {
        // Clear selection
        setSelectedOrganization(null);
        setSelectedOrganizationId(null);
        setAvailableLocations([]);
        setSelectedLocation(null);
        setSelectedLocationId(null);
        setOrgStats(null);
      }

      // Refresh user to update context
      await refreshUser();
    } catch (error) {
      console.error('Failed to select organization:', error);
      throw error;
    }
  }, [isSuperAdmin, organizations, fetchOrgStats, refreshUser]);

  // Select location
  const selectLocation = useCallback(async (locationId) => {
    if (!isSuperAdmin) return;

    try {
      await adminService.setAdminContext({ locationId: locationId || null });

      if (locationId) {
        const loc = availableLocations.find(l => l._id === locationId);
        setSelectedLocation(loc);
        setSelectedLocationId(locationId);
        localStorage.setItem('currentLocationId', locationId);
      } else {
        setSelectedLocation(null);
        setSelectedLocationId(null);
        localStorage.removeItem('currentLocationId');
      }

      // Refresh user
      await refreshUser();
    } catch (error) {
      console.error('Failed to select location:', error);
      throw error;
    }
  }, [isSuperAdmin, availableLocations, refreshUser]);

  // Refresh organizations list (and sync selected org / locations from server)
  const refreshOrganizations = useCallback(async () => {
    if (!isSuperAdmin) return;

    try {
      const response = await adminService.getAdminContext();
      const ctx = response.data || {};
      const orgs = ctx.organizations || [];
      setOrganizations(orgs);
      setAvailableLocations(ctx.locations || []);

      const sid =
        ctx.selectedOrganizationId?._id ?? ctx.selectedOrganizationId ?? selectedOrganizationId;
      if (sid) {
        const match = orgs.find((o) => String(o._id) === String(sid));
        if (match) setSelectedOrganization(match);
      }

      const lid =
        ctx.selectedLocationId?._id ?? ctx.selectedLocationId ?? selectedLocationId;
      if (lid && Array.isArray(ctx.locations) && ctx.locations.length > 0) {
        const locMatch = ctx.locations.find((l) => String(l._id) === String(lid));
        if (locMatch) {
          setSelectedLocation(locMatch);
          setSelectedLocationId(String(locMatch._id));
        }
      }
    } catch (error) {
      console.error('Failed to refresh organizations:', error);
    }
  }, [isSuperAdmin, selectedOrganizationId, selectedLocationId]);

  const value = useMemo(() => ({
    // State
    isSuperAdmin,
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    availableLocations,
    selectedLocation,
    selectedLocationId,
    platformStats,
    orgStats,
    loading,
    statsLoading,

    // Actions
    selectOrganization,
    selectLocation,
    fetchPlatformStats,
    fetchOrgStats,
    refreshOrganizations,
  }), [
    isSuperAdmin,
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    availableLocations,
    selectedLocation,
    selectedLocationId,
    platformStats,
    orgStats,
    loading,
    statsLoading,
    selectOrganization,
    selectLocation,
    fetchPlatformStats,
    fetchOrgStats,
    refreshOrganizations,
  ]);

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export default AdminContext;
